import {
    BlurEvent, ChangeEvent, DataSourceAction, DataSourceExecutionEvent, DataSourceMode, DataSourceModeChangeEvent, DropdownItem, LookupModelSearchEvent, LookupModelSearchListener, Textbox, TextboxProps
} from "@mcleod/components";
import { DatePart, DateUtil, LogManager } from "@mcleod/core";
import { LocationByTypeProps, LocationByTypeUtil } from "../LocationByTypeUtil";
import { AutogenLayoutOrderPlanningProfile } from "./autogen/AutogenLayoutOrderPlanningProfile";
import { LMEGeneralSettings } from "@mcleod/general";
import { TimelineZoomValidator } from "./timeline/TimelineZoomValidator";

const log = LogManager.getLogger("lme.dispatch.planning.OrderPlanningProfile");

const LOCATION_BY_TYPE_PROPS: LocationByTypeProps = {
    stateType: 'S',
    zoneType: 'Z',
    locationType: 'L',
    useDefault: true
}

const HIERARCHY_PROPS: Partial<TextboxProps> = {
    lookupModel: "lme/general/hierarchy",
    lookupModelLayout: "lme/dispatch/planning/OrderHierarchy",
    lookupModelLayoutWidth: 300,
    lookupModelResultField: "id",
    lookupModelDisplayField: "id"
}

export class OrderPlanningProfile extends AutogenLayoutOrderPlanningProfile {
    private orderLookupListener: LookupModelSearchListener;
    private tractorLookupListener: LookupModelSearchListener;
    private zoomValidator: TimelineZoomValidator;
    private LOCATION_FILTER_TYPE_ITEMS: DropdownItem[] = [
        new DropdownItem("Z", "Zone"),
        new DropdownItem("S", "States"),
        new DropdownItem("C", "Zip code"),
        new DropdownItem("L", "Location")
    ];

    onLoad() {
        this.textboxOrderIbType.items = this.LOCATION_FILTER_TYPE_ITEMS;
        this.textboxOrderObType.items = this.LOCATION_FILTER_TYPE_ITEMS;
        this.textboxTractorIbType.items = this.LOCATION_FILTER_TYPE_ITEMS;
        this.textboxTractorObType.items = this.LOCATION_FILTER_TYPE_ITEMS;

        LocationByTypeUtil.setupLocationTypeChangeListener(this.textboxOrderIbType, this.textboxOrderIbZones, LOCATION_BY_TYPE_PROPS);
        LocationByTypeUtil.setupLocationTypeChangeListener(this.textboxOrderObType, this.textboxOrderObZones, LOCATION_BY_TYPE_PROPS);
        LocationByTypeUtil.setupLocationTypeChangeListener(this.textboxTractorIbType, this.textboxTractorIbZones, LOCATION_BY_TYPE_PROPS);
        LocationByTypeUtil.setupLocationTypeChangeListener(this.textboxTractorObType, this.textboxTractorObZones, LOCATION_BY_TYPE_PROPS);

        this.textboxOrderRespLevel.addChangeListener((event: ChangeEvent) => {
            this.hierarchyLevelSelected(this.textboxOrderRespCodes, this.orderLookupListener, this.textboxOrderRespLevel.selectedItem, event.userInitiatedChange);
        });
        this.textboxTractorRespLevel.addChangeListener((event: ChangeEvent) => {
            this.hierarchyLevelSelected(this.textboxTractorRespCodes, this.tractorLookupListener, this.textboxTractorRespLevel.selectedItem, event.userInitiatedChange);
        });

        this.textboxOrderTractorFleetId.addBeforeLookupModelSearchListener(this.activeTractorFleetFilter);
        this.textboxTractorFleet.addBeforeLookupModelSearchListener(this.activeTractorFleetFilter);
        this.layoutProfileColor.profileDataSource = this.mainDataSource;
        this.switchIncludeOnHoldManifest.visible = LMEGeneralSettings.getSingleton().getDispatchControl()["enable_manifesting"] === "Y";
        this.zoomValidator = new TimelineZoomValidator(this.textboxCalendarZoomWeb);
        this.addVisualPlannerListeners();
    }

    sourceOrderPlanningProfileAfterModeChange(event: DataSourceModeChangeEvent) {
        if (event.newMode == DataSourceMode.UPDATE) {
            this.zoomValidator.setValues(this.getVisualPlannerDaysCalculated(), false);
        }
    }

    hierarchyLevelSelected(field: Textbox, listener: LookupModelSearchListener, selectedItem: DropdownItem, clearValues = true) {
        const selected = selectedItem?.value;
        if (clearValues) field.clear();
        field.removeBeforeLookupModelSearchListener(listener);
        if (selected && selected !== "<blank>") {
            listener = (event:LookupModelSearchEvent) => {
                event.filter.hierarchy_level = selected;
            };
            field.addBeforeLookupModelSearchListener(listener)
            field.setProps(HIERARCHY_PROPS);
            this.mainDataSource.displayDataInBoundComponent(field);
        } else {
            LocationByTypeUtil.setDefaultFreeform(field);
        }
    }

    private activeTractorFleetFilter(event:LookupModelSearchEvent) {
        event.filter.is_tractor = 'Y';
        event.filter.is_active = 'Y';
    }

    /** This is an event handler for the beforeLookupModelSearch event of textboxOrderType. */
    textboxOrderTypeBeforeLookupModelSearch(event) {
        event.filter.is_active = 'Y';
    }

    /* Event handler for visual planner components */
    addVisualPlannerListeners() {
        this.daterangefilterCalendarDates.textboxStart.addChangeListener(this.vpDateOnChange.bind(this));
        this.daterangefilterCalendarDates.textboxEndDate.addChangeListener(this.vpDateOnChange.bind(this));
        this.daterangefilterCalendarDates.textboxStart.addBlurListener(this.vpDateOnBlur.bind(this));
        this.daterangefilterCalendarDates.textboxEndDate.addBlurListener(this.vpDateOnBlur.bind(this));
    }

    vpDateOnBlur(event: BlurEvent) {
        const { textboxStart, textboxEndDate } = this.daterangefilterCalendarDates;
        if (!event.changedWhileFocused || textboxStart.validationWarning || textboxEndDate.validationWarning) {
            return;
        }
        this.zoomValidator.setValues(this.getVisualPlannerDaysCalculated(), true);
        this.activeRow.set("calendar_zoom_web", this.textboxCalendarZoomWeb.selectedItem.value);
    }

    vpDateOnChange(event: ChangeEvent) {
        if (event.newValue.length < 8 || !event.userInitiatedChange) {
            return;
        }
        if ((event.target as Textbox).validateSimple()) {
            this.zoomValidator.setValues(this.getVisualPlannerDaysCalculated(), true);
            this.activeRow.set("calendar_zoom_web", this.textboxCalendarZoomWeb.selectedItem.value);
        }
    }

    getVisualPlannerDaysCalculated() {
        return DateUtil.dateDiff(DatePart.DAY, this.daterangefilterCalendarDates.endDate, this.daterangefilterCalendarDates.startDate) + 1;
    }
}
